import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, FileText, Download, Eye, Image as ImageIcon, Film, FileSpreadsheet, Folder, Loader2, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { BASE_URL } from '@/lib/API';
import { designService } from '../../../client_contact_stages/verve-design/services/designService';
import { leadService } from '../../../verve-leads/leads/services/leadService';
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { useAuthStore } from '@/store/authStore';

export const InitialDesignAccordion = ({ lead, onPreview, onLeadUpdate }) => {
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [designMediaState, setDesignMediaState] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (lead?.designMedia && lead.designMedia.length > 0) {
      setDesignMediaState(lead.designMedia);
    } else if (isOpen && lead?._id) {
      const fetchDesign = async () => {
        try {
          setIsLoading(true);
          const response = await designService.getDesignByLeadId(lead._id);
          if (response.data && response.data.media) {
            setDesignMediaState(response.data.media);
          }
        } catch (error) {
          console.error('Error fetching design media:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchDesign();
    }
  }, [isOpen, lead]);

  const handleDelete = async (fileId, filename, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete ${filename}? This action cannot be undone and will remove the file from Google Drive.`)) {
      return;
    }

    try {
      setIsLoading(true);
      await designService.deleteDesignMedia(lead._id, fileId);
      showSuccessMessage(`Deleted ${filename} successfully`);
      setDesignMediaState(prev => prev.filter(f => f.fileId !== fileId));
    } catch (err) {
      showApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!lead) {
    return null;
  }

  const designMedia = designMediaState.filter(f =>
    f.stage === 'initial_design' ||
    (!f.stage && !f.filename?.toLowerCase().replace(/_/g, ' ').includes('sign off') && !f.filename?.toLowerCase().replace(/_/g, ' ').includes('prod') && !f.filename?.toLowerCase().replace(/_/g, ' ').includes('invoice') && !f.filename?.toLowerCase().replace(/_/g, ' ').includes('quotation'))
  );
  const hasData = designMedia.length > 0;

  const handleApprovalAction = async (e, status) => {
    e.stopPropagation();
    try {
      setIsLoading(true);
      const res = await leadService.update(lead._id, { mainDesignerApprovalStatus: status });
      showSuccessMessage(`Design ${status.toLowerCase()} successfully`);
      if (onLeadUpdate) {
        onLeadUpdate(res.data);
      } else {
        // Fallback to reload if callback not provided
        window.location.reload();
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const isMainDesigner = user && lead && (
    lead.mainDesigner?._id === user._id || lead.mainDesigner === user._id || lead.mainDesigner?.id === user.id || lead.mainDesigner === user.id ||
    lead.siteExecutive?._id === user._id || lead.siteExecutive === user._id || lead.siteExecutive?.id === user.id || lead.siteExecutive === user.id
  );
  const showApprovalButtons = lead?.mainDesignerApprovalStatus === 'Pending' && isMainDesigner;

  return (
    <div className="bg-pink-50/40 rounded-xl border border-pink-100/60 overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-pink-50/60 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-col gap-1">
          <div className="text-[10px] text-pink-600 font-bold uppercase tracking-wider flex items-center gap-2">
            <span className="w-1 h-3 bg-pink-500 rounded-full" />
            Initial Designs {hasData ? `Available (${designMedia.length} files)` : 'Not Available'}
          </div>
          {lead?.mainDesignerApprovalStatus && (
            <div className={`text-[10px] font-semibold px-2 py-0.5 rounded-full self-start ${lead.mainDesignerApprovalStatus === 'Approved' ? 'bg-emerald-100 text-emerald-700' :
              lead.mainDesignerApprovalStatus === 'Rejected' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
              }`}>
              Approval: {lead.mainDesignerApprovalStatus}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {showApprovalButtons && (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => handleApprovalAction(e, 'Approved')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200 rounded-md text-xs font-semibold transition-colors shadow-sm"
              >
                <CheckCircle size={14} />
                Approve
              </button>
              <button
                onClick={(e) => handleApprovalAction(e, 'Rejected')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-md text-xs font-semibold transition-colors shadow-sm"
              >
                <XCircle size={14} />
                Reject
              </button>
            </div>
          )}
          {isOpen ? <ChevronUp size={16} className="text-pink-400" /> : <ChevronDown size={16} className="text-pink-400" />}
        </div>
      </div>

      {isOpen && (
        <div className="px-4 pb-4 border-t border-pink-100/50 pt-3">
          {isLoading ? (
            <div className="flex items-center justify-center p-4 text-pink-500">
              <Loader2 className="animate-spin" size={20} />
              <span className="ml-2 text-xs font-medium">Loading designs...</span>
            </div>
          ) : hasData ? (
            <div className="flex flex-col gap-3">
              <div className="border border-slate-200 bg-white rounded p-3">
                <h5 className="text-[11px] font-bold text-slate-600 mb-2 border-b pb-1">Uploaded Designs</h5>
                <div className="flex flex-col gap-1.5">
                  {designMedia.map((file, idx) => {
                    const isImage = file.mimetype?.startsWith('image/');
                    const isVideo = file.mimetype?.startsWith('video/');
                    const isPdf = file.mimetype === 'application/pdf';
                    const isExcel = file.mimetype?.includes('spreadsheet') || file.mimetype?.includes('excel') || file.filename?.endsWith('.xls') || file.filename?.endsWith('.xlsx');

                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-[11px] text-pink-600 bg-slate-50 p-2 rounded border border-slate-100 transition-colors w-full text-left group hover:bg-slate-100"
                      >
                        {isImage ? <ImageIcon size={14} className="text-blue-500 shrink-0" /> :
                          isVideo ? <Film size={14} className="text-purple-500 shrink-0" /> :
                            isPdf ? <FileText size={14} className="text-red-500 shrink-0" /> :
                              isExcel ? <FileSpreadsheet size={14} className="text-emerald-500 shrink-0" /> :
                                <Folder size={14} className="text-amber-500 shrink-0" />}
                        <span className="truncate flex-1" title={file.filename}>{file.filename}</span>

                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onPreview) {
                                onPreview(file);
                              } else {
                                const url = `${BASE_URL}/api/design/media/${file.fileId}`;
                                window.open(url, '_blank');
                              }
                            }}
                            className="w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-400 hover:text-brand-blue transition-all border border-slate-100 shadow-sm"
                            title="Preview"
                          >
                            <Eye size={12} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              import('@/lib/API').then(({ downloadFile }) => {
                                const url = `${BASE_URL}/api/design/media/${file.fileId}`;
                                downloadFile(url, file.filename);
                              });
                            }}
                            className="w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-400 hover:text-emerald-600 transition-all border border-slate-100 shadow-sm"
                            title="Download"
                          >
                            <Download size={12} />
                          </button>
                          <button
                            onClick={(e) => handleDelete(file.fileId, file.filename, e)}
                            className="w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-400 hover:text-red-600 transition-all border border-slate-100 shadow-sm"
                            title="Delete from Drive"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 italic text-center py-2">
              No initial designs have been uploaded yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
